if (!customElements.get('accordion-item')) {
  class AccordionItem extends HTMLElement {
    constructor() {
      super();

      this.handleClick = this.toggle.bind(this);
      this.handleOpenAttributes = this.updateAttributes.bind(this, true);
      this.handleCloseAttributes = this.updateAttributes.bind(this, false);
      this.handleResize = debounce(this.onResize.bind(this), RESIZE_DEBOUNCE_TIMER);
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    get group() {
      return this.getAttribute('group') || null;
    }

    get observe() {
      const attr = this.getAttribute('observe');
      return attr !== undefined && attr !== 'false';
    }

    set group(val) {
      if (val) {
        this.setAttribute('group', val);
      } else {
        this.removeAttribute('group');
      }
    }

    set observe(val) {
      if (val) {
        this.setAttribute('observe', val);
      } else {
        this.removeAttribute('observe');
      }
    }

    initObserver() {
      if (!this.observe) return;

      this.observer = new MutationObserver((mutationList, observer) => {
        for (const mutation of mutationList) {
          if (mutation.type === 'childList') {
            this.reset();
          }
        }
      });

      this.observer.observe(this.details, {
        childList: true,
      });
    }

    buildTimeline() {
      return gsap
        .timeline({
          paused: true,
          onStart: this.handleOpenAttributes,
          onReverseComplete: this.handleCloseAttributes,
        })
        .fromTo(
          this.content,
          {
            height: 0,
            autoAlpha: 0,
          },
          {
            height: 'auto',
            autoAlpha: 1,
            duration: 0.4,
            ease: 'power2.inOut',
          }
        );
    }

    init(initObserver = true) {
      this.container = this.parentElement;
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.summary.nextElementSibling;
      if (this.group) {
        this.siblings = Array.from(
          this.container.querySelectorAll(`accordion-item[group="${this.group}"]`)
        ).filter(child => child !== this);
      }
      this.isFirst = this.container.children[0] === this;

      this.timeline = this.buildTimeline();
      this.summary.addEventListener('click', this.handleClick);
      window.addEventListener('resize', this.handleResize);
      this.checkUrlHash();

      if (this.isOpen()) this.open(false, true);

      if (initObserver) this.initObserver();
    }

    checkUrlHash() {
      const hash = window.location.hash.replace('#', '');

      if (
        hash === this.details.id.replace('Accordion-', '') ||
        (this.isFirst && hash === this.group)
      ) {
        this.details.setAttribute('open', true);
      }
    }

    destroy(destroyObserver = true) {
      this.timeline.kill();
      this.summary.removeEventListener('click', this.handleClick);
      window.removeEventListener('resize', this.handleResize);

      if (destroyObserver) this.observer?.disconnect();
    }

    reset() {
      this.destroy(false);
      this.init(false);
    }

    isOpen() {
      return this.details.hasAttribute('open');
    }

    open(closeSiblings = true, instant = false) {
      this.timeline?.invalidate();
      if (closeSiblings) {
        this.siblings?.forEach(sibling => sibling.close());
      }

      if (instant) {
        this.timeline.progress(1);
      } else {
        this.timeline.play();
      }
    }

    close(instant = false) {
      this.timeline?.invalidate();
      this.details.classList.remove('is-open');
      
      if (instant) {
        this.timeline.progress(0);
      } else {
        this.timeline.reverse();
      }
    }

    updateAttributes(open = true) {
      this.details.classList.toggle('is-open', open);
      this.details.toggleAttribute('open', open);
      this.summary.setAttribute('aria-expanded', open);
    }

    toggle(e) {
      e?.preventDefault();

      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    }

    onResize() {
      this.timeline?.invalidate();
    }
  }

  customElements.define('accordion-item', AccordionItem);
}